import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Table, Button, Alert, 
  Badge, InputGroup, Form, Dropdown
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaUsers, FaCogs, FaUserTie, FaKey } from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import AdminLayout from '../../components/layout/AdminLayout';
import UserModal from '../../components/modals/UserModal';
import supabase from '../../utils/supabaseClient';

const UserManagement = () => {
  const { user, isAdmin } = useAuth();
  
  // Estados principais
  const [usuarios, setUsuarios] = useState([]);
  const [systems, setSystems] = useState([]);
  const [systemRoles, setSystemRoles] = useState([]);
  const [vendedores, setVendedores] = useState([]); // Novo estado para vendedores
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Estados do modal
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);

  // Estados para filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [systemFilter, setSystemFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendedorFilter, setVendedorFilter] = useState('all'); // Novo filtro para vendedores

  // Carregar dados iniciais
  useEffect(() => {
    if (isAdmin) {
      loadAllData();
    }
  }, [isAdmin]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadUsuarios(),
        loadSystems(),
        loadSystemRoles(),
        loadVendedores() // Carregar vendedores
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setError('Erro ao carregar dados do sistema');
    } finally {
      setLoading(false);
    }
  };

 const loadUsuarios = async () => {
    try {
      // Carregar perfis com seus acessos e vendedores vinculados
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      // Para cada profile, carregar seus acessos e vendedor vinculado
      const usersWithAccessAndVendedor = await Promise.all(
        profiles.map(async (profile) => {
          // Carregar acessos aos sistemas
          const { data: userAccess, error: accessError } = await supabase
            .from('user_system_access')
            .select(`
              id,
              system_id,
              role_id,
              active,
              expires_at,
              systems:system_id (
                id,
                name,
                slug,
                icon
              ),
              system_roles:role_id (
                id,
                name,
                description
              )
            `)
            .eq('profile_id', profile.id)
            .eq('active', true);

          // Carregar vendedor vinculado - método simplificado para evitar erros de foreign key
          let vendedorVinculado = null;
          try {
            const { data: userVendedorData, error: vendedorError } = await supabase
              .from('user_vendedores')
              .select('vendedor_id')
              .eq('profile_id', profile.id)
              .maybeSingle(); // Usa maybeSingle ao invés de single para evitar erro quando não encontra

            if (!vendedorError && userVendedorData?.vendedor_id) {
              // Buscar dados do vendedor separadamente
              const { data: vendedorData, error: vendedorDetailsError } = await supabase
                .from('vendedores')
                .select('id, descricao, ativo')
                .eq('id', userVendedorData.vendedor_id)
                .single();

              if (!vendedorDetailsError && vendedorData) {
                vendedorVinculado = {
                  vendedor_id: userVendedorData.vendedor_id,
                  vendedores: vendedorData
                };
              }
            }
          } catch (vendedorError) {
            // Se houver erro ao carregar vendedor, apenas ignore
            console.warn('Erro ao carregar vendedor para usuário:', profile.id, vendedorError);
          }

          return {
            ...profile,
            systemAccesses: accessError ? [] : (userAccess || []),
            vendedorVinculado
          };
        })
      );

      setUsuarios(usersWithAccessAndVendedor);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      throw error;
    }
  };

  const loadSystems = async () => {
    try {
      const { data, error } = await supabase
        .from('systems')
        .select('*')
        .eq('active', true)
        .order('name');

      if (error) throw error;
      setSystems(data || []);
    } catch (error) {
      console.error('Erro ao carregar sistemas:', error);
      throw error;
    }
  };

  const loadSystemRoles = async () => {
    try {
      const { data, error } = await supabase
        .from('system_roles')
        .select(`
          *,
          systems:system_id (
            id,
            name,
            slug
          )
        `)
        .eq('active', true)
        .order('system_id, name');

      if (error) throw error;
      setSystemRoles(data || []);
    } catch (error) {
      console.error('Erro ao carregar roles dos sistemas:', error);
      throw error;
    }
  };

  const loadVendedores = async () => {
    try {
      const { data, error } = await supabase
        .from('vendedores')
        .select('*')
        .eq('ativo', true)
        .order('descricao');

      if (error) throw error;
      setVendedores(data || []);
    } catch (error) {
      console.error('Erro ao carregar vendedores:', error);
      throw error;
    }
  };

  // Abrir modal
  const openModal = (type, usuario = null) => {
    setModalType(type);
    setSelectedUser(usuario);
    setShowModal(true);
  };

  // Fechar modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setModalType('create');
  };

  // Success callback do modal
  const handleModalSuccess = (message) => {
    setSuccess(message);
    loadAllData();
    setTimeout(() => setSuccess(null), 5000);
  };

  // Alterar senha do usuário
  const handleChangePassword = async (userId, userName) => {
    const newPassword = prompt(`Digite a nova senha para "${userName}":\n\nA senha deve ter pelo menos 6 caracteres.`);
    
    if (!newPassword) {
      return; // Usuário cancelou
    }

    if (newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (!window.confirm(`Confirma a alteração da senha para "${userName}"?`)) {
      return;
    }

    try {
      setLoading(true);

      // Obter o token de autorização
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Sessão não encontrada');
      }

      // Chamar a edge function
      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/update-user-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId,
          newPassword: newPassword
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erro ao alterar senha');
      }

      setSuccess(`Senha do usuário "${userName}" alterada com sucesso!`);
      setTimeout(() => setSuccess(null), 5000);

    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      setError(`Erro ao alterar senha: ${error.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Excluir usuário
  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${userName}"?\n\nEsta ação irá:\n• Remover o usuário do sistema\n• Remover todos os acessos\n• Remover vinculação com vendedor (o vendedor permanecerá ativo)\n\nNOTA: Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      setLoading(true);
      
      // Remover vinculação com vendedor
      await supabase
        .from('user_vendedores')
        .delete()
        .eq('profile_id', userId);
      
      // Remover acessos
      await supabase
        .from('user_system_access')
        .delete()
        .eq('profile_id', userId);

      // Remover perfil
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);

      if (error) throw error;

      setSuccess(`Usuário "${userName}" removido com sucesso!`);
      await loadAllData();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      setError(`Erro ao excluir usuário: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Verificar se usuário tem role de vendedor
  const hasVendedorRole = (usuario) => {
    return usuario.systemAccesses?.some(access => 
      access.system_roles?.name?.toLowerCase() === 'vendedor' ||
      access.system_roles?.description?.toLowerCase().includes('vendedor')
    );
  };

  // Filtrar usuários
  const filteredUsuarios = usuarios.filter(usuario => {
    const matchesSearch = 
      usuario.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      usuario.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesSystem = systemFilter === 'all' || 
      usuario.systemAccesses?.some(access => access.systems?.slug === systemFilter);

    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && usuario.active !== false) ||
      (statusFilter === 'inactive' && usuario.active === false);

    const matchesVendedor = vendedorFilter === 'all' || 
      (vendedorFilter === 'with-vendedor' && usuario.vendedorVinculado) ||
      (vendedorFilter === 'without-vendedor' && !usuario.vendedorVinculado) ||
      (vendedorFilter === 'vendedor-role' && hasVendedorRole(usuario));

    return matchesSearch && matchesSystem && matchesStatus && matchesVendedor;
  });

  const formatarData = (dataString) => {
    return new Date(dataString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (!isAdmin) {
    return (
      <div className="text-center mt-5">
        <h3>Acesso Negado</h3>
        <p>Você não tem permissão para acessar esta área.</p>
      </div>
    );
  }

  return (
    <AdminLayout>
      <Container fluid>
        {/* Cabeçalho */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="mb-1">
                  <FaUsers className="me-2" />
                  Gerenciamento de Usuários
                </h1>
                <p className="text-muted mb-0">
                  Gerencie usuários, seus acessos aos sistemas e vinculações com vendedores (obrigatório para role Vendedor)
                </p>
              </div>
              
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-primary" 
                  onClick={loadAllData}
                  disabled={loading}
                >
                  🔄 Atualizar
                </Button>
                <Button 
                  variant="primary" 
                  onClick={() => openModal('create')}
                >
                  <FaPlus className="me-2" />
                  Novo Usuário
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Mensagens */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Filtros */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={3}>
                <InputGroup>
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por nome ou email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={2}>
                <Form.Select
                  value={systemFilter}
                  onChange={(e) => setSystemFilter(e.target.value)}
                >
                  <option value="all">Todos os sistemas</option>
                  {systems.map(system => (
                    <option key={system.id} value={system.slug}>
                      {system.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Todos os status</option>
                  <option value="active">Apenas ativos</option>
                  <option value="inactive">Apenas inativos</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={vendedorFilter}
                  onChange={(e) => setVendedorFilter(e.target.value)}
                >
                  <option value="all">Todos os usuários</option>
                  <option value="with-vendedor">Com vendedor vinculado</option>
                  <option value="without-vendedor">Sem vendedor vinculado</option>
                  <option value="vendedor-role">Com role Vendedor</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Button 
                  variant="outline-secondary" 
                  onClick={() => {
                    setSearchTerm('');
                    setSystemFilter('all');
                    setStatusFilter('all');
                    setVendedorFilter('all');
                  }}
                >
                  Limpar
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Estatísticas rápidas */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="border-0 bg-primary text-white">
              <Card.Body className="text-center">
                <h4>{usuarios.length}</h4>
                <small>Total de Usuários</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 bg-success text-white">
              <Card.Body className="text-center">
                <h4>{usuarios.filter(u => u.active !== false).length}</h4>
                <small>Usuários Ativos</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 bg-warning text-white">
              <Card.Body className="text-center">
                <h4>{usuarios.filter(u => hasVendedorRole(u)).length}</h4>
                <small>Com Role Vendedor</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 bg-info text-white">
              <Card.Body className="text-center">
                <h4>{usuarios.filter(u => u.vendedorVinculado).length}</h4>
                <small>Com Vendedor Vinculado</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabela de Usuários */}
        <Card>
          <Card.Header>
            <h5 className="mb-0">
              Usuários Cadastrados ({filteredUsuarios.length})
            </h5>
          </Card.Header>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Usuário</th>
                  <th>Status</th>
                  <th>Sistemas com Acesso</th>
                  <th>Vendedor Vinculado</th>
                  <th>Criado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsuarios.map(usuario => (
                  <tr key={usuario.id}>
                    <td>
                      <div>
                        <strong>{usuario.full_name || 'Nome não informado'}</strong>
                        <br />
                        <small className="text-muted">{usuario.email}</small>
                      </div>
                    </td>
                    <td>
                      <Badge bg={usuario.active !== false ? 'success' : 'danger'}>
                        {usuario.active !== false ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {usuario.systemAccesses?.length > 0 ? (
                          usuario.systemAccesses.map(access => (
                            <Badge 
                              key={access.id} 
                              bg={access.systems?.slug === 'portal-vendas' ? 'warning' : 'primary'}
                              title={`${access.systems.name} - ${access.system_roles.name}`}
                            >
                              {access.systems.icon} {access.systems.name}
                            </Badge>
                          ))
                        ) : (
                          <Badge bg="secondary" text="dark">Sem acesso</Badge>
                        )}
                      </div>
                    </td>
                    <td>
                      {usuario.vendedorVinculado?.vendedores ? (
                        <div className="d-flex align-items-center">
                          <Badge bg="info" className="d-flex align-items-center">
                            <FaUserTie className="me-1" />
                            {usuario.vendedorVinculado.vendedores.descricao}
                          </Badge>
                          {hasVendedorRole(usuario) && (
                            <Badge bg="success" className="ms-1" title="Vinculação necessária para role Vendedor">
                              ✓
                            </Badge>
                          )}
                        </div>
                      ) : hasVendedorRole(usuario) ? (
                        <Badge bg="danger" title="Role Vendedor requer vinculação com vendedor">
                          ⚠️ Requer vendedor
                        </Badge>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{formatarData(usuario.created_at)}</td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                          Ações
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => openModal('edit', usuario)}>
                            <FaEdit className="me-2" />
                            Editar
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleChangePassword(usuario.id, usuario.full_name || usuario.email)}>
                            <FaKey className="me-2" />
                            Alterar Senha
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item 
                            className="text-danger"
                            onClick={() => handleDeleteUser(usuario.id, usuario.full_name || usuario.email)}
                          >
                            <FaTrash className="me-2" />
                            Excluir
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {filteredUsuarios.length === 0 && !loading && (
              <div className="text-center py-4">
                <p className="text-muted">Nenhum usuário encontrado com os filtros aplicados.</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Modal de Usuário */}
        <UserModal
          show={showModal}
          onHide={closeModal}
          modalType={modalType}
          selectedUser={selectedUser}
          systems={systems}
          systemRoles={systemRoles}
          onSuccess={handleModalSuccess}
        />
      </Container>
    </AdminLayout>
  );
};

export default UserManagement;